import * as XLSX from 'xlsx';

const getGlAccountLabel = (glAccountId, glAccounts = []) => {
  if (!glAccountId) return '';
  const account = (glAccounts || []).find(
    (item) => String(item.ledgerAccountId) === String(glAccountId)
  );
  if (!account) return String(glAccountId);
  return account.glAccount || `${account.glCode || ''} - ${account.glDescription || ''}`.trim();
};

const getGlCode = (glAccountId, glAccounts = []) => {
  if (!glAccountId) return '';
  const account = (glAccounts || []).find(
    (item) => String(item.ledgerAccountId) === String(glAccountId)
  );
  return account?.glCode != null ? String(account.glCode) : '';
};

const getGlDescription = (glAccountId, glAccounts = []) => {
  if (!glAccountId) return '';
  const account = (glAccounts || []).find(
    (item) => String(item.ledgerAccountId) === String(glAccountId)
  );
  return account?.glDescription != null ? String(account.glDescription) : '';
};

const formatExportDate = (value, formatDateOnly, dateFormat) => {
  if (!value) return '';
  if (typeof formatDateOnly === 'function') {
    return formatDateOnly(value, dateFormat) || String(value);
  }
  return String(value);
};

const formatExportAmount = (value) => {
  if (value == null || value === '') return '';
  const num = Number(value);
  return Number.isFinite(num) ? num : value;
};

/**
 * Build denormalized export rows (one row per line, or per split when present).
 * @param {Array} invoiceRows - list rows with { raw, vendorName, businessUnitName, displayStatus }
 */
export const buildInvoiceExportRows = (
  invoiceRows = [],
  {
    lines = [],
    splits = [],
    glAccounts = [],
    formatDateOnly,
    dateFormat,
  } = {}
) => {
  const linesByInvoiceId = (Array.isArray(lines) ? lines : []).reduce((acc, line) => {
    const id = line.apInvoiceId;
    if (!id) return acc;
    if (!acc[id]) acc[id] = [];
    acc[id].push(line);
    return acc;
  }, {});

  const splitsByLineId = (Array.isArray(splits) ? splits : []).reduce((acc, split) => {
    const id = split.apInvoiceLineId;
    if (!id) return acc;
    if (!acc[id]) acc[id] = [];
    acc[id].push(split);
    return acc;
  }, {});

  const rows = [];

  (invoiceRows || []).forEach((invoiceRow) => {
    const inv = invoiceRow.raw || invoiceRow;
    const invoiceId = inv.apInvoiceId || invoiceRow.apInvoiceId;
    const invoiceLines = linesByInvoiceId[invoiceId] || [];

    const headerBase = {
      'Invoice Number': inv.invoiceNumber || '',
      Vendor: invoiceRow.vendorName || inv.vendorName || '',
      'Business Unit': invoiceRow.businessUnitName || '',
      'Invoice Date': formatExportDate(inv.invoiceDate, formatDateOnly, dateFormat),
      'Due Date': formatExportDate(inv.invoiceDueDate, formatDateOnly, dateFormat),
      Currency: inv.currency || '',
      'PO Reference': inv.purchaseOrderNumber || inv.poReference || '',
      Subtotal: formatExportAmount(inv.invoiceSubtotal),
      'Tax Amount': formatExportAmount(inv.invoiceTotalTaxes),
      'Shipping / Freight': formatExportAmount(inv.invoiceTotalFees),
      'Total Amount': formatExportAmount(inv.invoiceTotalAmountDue),
      'Amount Paid': formatExportAmount(inv.amountPaid),
      'Remaining Balance': formatExportAmount(inv.remainingBalance),
      Status: invoiceRow.displayStatus || inv.workflowStatus || '',
      'Workflow Status': inv.workflowStatus || '',
      'Payment Status': inv.paymentStatus || '',
      'Header GL Code': inv.glCode || '',
    };

    if (!invoiceLines.length) {
      rows.push({
        ...headerBase,
        'Line #': '',
        Description: '',
        Qty: '',
        'Unit Price': '',
        'Line Total': '',
        'Part/SKU': '',
        Split: '',
        'GL Code': '',
        'GL Description': '',
        'GL Account': '',
        'Split Amount': '',
      });
      return;
    }

    invoiceLines.forEach((line, index) => {
      const lineSplits = splitsByLineId[line.apInvoiceLineId] || [];
      const lineBase = {
        ...headerBase,
        'Line #': line.lineItemNumber || String(index + 1),
        Description: line.lineItemDescription || '',
        Qty: line.quantity != null ? Number(line.quantity) : '',
        'Unit Price': formatExportAmount(line.lineItemRate),
        'Line Total': formatExportAmount(line.lineItemTotal),
        'Part/SKU': line.productCode || '',
        Split: line.isSplit ? 'Yes' : 'No',
      };

      if (lineSplits.length) {
        lineSplits.forEach((split) => {
          rows.push({
            ...lineBase,
            'GL Code': getGlCode(split.glAccountId, glAccounts),
            'GL Description': getGlDescription(split.glAccountId, glAccounts),
            'GL Account': getGlAccountLabel(split.glAccountId, glAccounts),
            'Split Amount': formatExportAmount(split.splitAmount),
          });
        });
        return;
      }

      rows.push({
        ...lineBase,
        'GL Code': getGlCode(line.glAccountId, glAccounts),
        'GL Description': getGlDescription(line.glAccountId, glAccounts),
        'GL Account': getGlAccountLabel(line.glAccountId, glAccounts),
        'Split Amount': '',
      });
    });
  });

  return rows;
};

export const downloadInvoiceExport = (exportRows, { format = 'xlsx', fileBaseName = 'invoices' } = {}) => {
  const stamp = new Date().toISOString().slice(0, 10);
  const base = `${fileBaseName}-${stamp}`;

  if (format === 'csv') {
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${base}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return;
  }

  const ws = XLSX.utils.json_to_sheet(exportRows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Invoices');
  XLSX.writeFile(wb, `${base}.xlsx`);
};
